import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { TextField, SelectField } from "@/components/common";
import { PROPERTY_TYPE, usd } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Search, Radar, Plus, MapPin, Bookmark, X, PhoneCall, Phone, Mail, Bell, BellRing, RefreshCw, Layers, AlertTriangle, Database } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ProspectDatabase from "@/pages/prospecting/ProspectDatabase";

// Extracts a readable message from an API error, including the structured 429 limit payload.
function apiErr(e, fallback) {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object") {
    const reset = d.reset_at ? ` Resets ${new Date(d.reset_at).toLocaleString()}.` : "";
    return (d.message || "Limit reached.") + reset;
  }
  return d || fallback;
}

const EQUITY_LEVELS = ["Any equity", "Some equity ($50k+)", "High equity (50%+ of value)"];
const EQ_MAP = { "Some equity ($50k+)": "some", "High equity (50%+ of value)": "high" };

export default function Prospecting() {
  const [f, setF] = useState({ postalcode: "", property_type: "", min_beds: "", min_baths: "", absentee: false, owner_occupied: false, min_equity: "", equity_level: "", min_last_sold: "", max_last_sold: "" });
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(null);
  const [saved, setSaved] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [contacts, setContacts] = useState({}); // attom_id -> {phones, emails, mocked}
  const [tracing, setTracing] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [svEnabled, setSvEnabled] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const nav = useNavigate();

  const loadSaved = () => api.get("/prospecting/saved").then((r) => setSaved(r.data)).catch(() => {});
  useEffect(() => { loadSaved(); api.get("/streetview/status").then((r) => setSvEnabled(!!r.data.enabled)).catch(() => {}); }, []);

  const runSearch = async (filters) => {
    if (!filters.postalcode?.trim?.()) return toast.error("Enter a ZIP / postal code to search");
    setBusy(true); setResults(null); setContacts({}); setSelected(new Set());
    try {
      const params = { postalcode: filters.postalcode.trim() };
      if (filters.property_type) params.property_type = filters.property_type;
      if (filters.min_beds) params.min_beds = Number(filters.min_beds);
      if (filters.min_baths) params.min_baths = Number(filters.min_baths);
      if (filters.absentee) params.absentee = true;
      if (filters.owner_occupied && !filters.absentee) params.owner_occupied = true;
      if (filters.min_last_sold) params.min_last_sold = Number(filters.min_last_sold);
      if (filters.max_last_sold) params.max_last_sold = Number(filters.max_last_sold);
      if (filters.min_equity) params.min_equity = Number(filters.min_equity);
      if (filters.equity_level && EQ_MAP[filters.equity_level]) params.equity_level = EQ_MAP[filters.equity_level];
      const { data } = await api.get("/prospecting/search", { params });
      setResults(data);
      if (data.count === 0) toast.message(data.note || "No properties matched those filters.");
      else toast.success(`Found ${data.count} properties in ${filters.postalcode}`);
    } catch (err) {
      toast.error(apiErr(err, "Search failed"));
      setResults({ results: [], count: 0 });
    } finally { setBusy(false); }
  };
  const search = (e) => { e?.preventDefault?.(); runSearch(f); };

  const saveSearch = async () => {
    if (!saveName.trim()) return toast.error("Name your search");
    try {
      await api.post("/prospecting/saved", { name: saveName.trim(), filters: f });
      toast.success("Search saved"); setSaveOpen(false); setSaveName(""); loadSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  const applySaved = (s) => { setF({ postalcode: "", property_type: "", min_beds: "", min_baths: "", absentee: false, owner_occupied: false, min_equity: "", equity_level: "", min_last_sold: "", max_last_sold: "", ...s.filters }); runSearch(s.filters); };
  const deleteSaved = async (s) => { try { await api.delete(`/prospecting/saved/${s.id}`); loadSaved(); } catch { toast.error("Could not delete"); } };
  const toggleAlerts = async (s) => {
    try {
      await api.post(`/prospecting/saved/${s.id}/toggle-alerts`, { enabled: !s.alerts_enabled });
      toast.success(!s.alerts_enabled ? `Alerts on for "${s.name}"` : `Alerts off for "${s.name}"`);
      loadSaved();
    } catch { toast.error("Could not update alerts"); }
  };
  const checkNow = async (s) => {
    try {
      const { data } = await api.post(`/prospecting/saved/${s.id}/check`);
      toast.success(data.new_count > 0 ? `${data.new_count} new match${data.new_count === 1 ? "" : "es"} found` : "No new matches yet");
    } catch (e) { toast.error(e.response?.data?.detail || "Check failed"); }
  };

  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) { n.delete(id); } else { n.add(id); } return n; });
  const toggleSelectAll = () => setSelected((s) => {
    const ids = (results?.results || []).map((r) => r.attom_id);
    return s.size === ids.length ? new Set() : new Set(ids);
  });
  const bulkAdd = async () => {
    const chosen = (results?.results || []).filter((r) => selected.has(r.attom_id));
    if (!chosen.length) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/prospecting/bulk-add", { property_type: f.property_type, properties: chosen, contacts });
      const msg = `${data.created} lead${data.created === 1 ? "" : "s"} added to your pipeline` + (data.skipped ? ` · ${data.skipped} duplicate${data.skipped === 1 ? "" : "s"} skipped` : "");
      toast.success(msg);
      setSelected(new Set());
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk add failed"); }
    finally { setBulkBusy(false); }
  };

  const skipTrace = async (p) => {
    setTracing(p.attom_id);
    try {
      const { data } = await api.post("/prospecting/skip-trace", { owner_name: p.owner_name, property_address: p.property_address, city: p.city, state: p.state, zip_code: p.zip_code });
      setContacts((c) => ({ ...c, [p.attom_id]: data }));
      if (data.mocked) toast.message("Demo skip-trace — connect a provider for real contacts");
      else if (data.no_match) toast.message("No contact records found for this property");
      else toast.success("Contact found");
    } catch (e) { toast.error(apiErr(e, "Skip-trace failed")); }
    finally { setTracing(null); }
  };

  const [savingProspect, setSavingProspect] = useState(null);
  const saveProspect = async (p) => {
    setSavingProspect(p.attom_id);
    try {
      await api.post("/prospects", {
        property_address: p.property_address, city: p.city, state: p.state, zip_code: p.zip_code,
        county: p.county, apn: p.parcel_number, property_type: p.property_type || f.property_type,
        owner_name: p.owner_name, estimated_value: p.estimated_market_value,
        estimated_mortgage_balance: p.estimated_loan_balance, estimated_equity: p.estimated_equity,
        beds: p.bedrooms, baths: p.bathrooms, living_sqft: p.square_feet, year_built: p.year_built,
        distress_type: p.absentee ? "Absentee Owner" : "", source: "ATTOM",
      });
      toast.success("Prospect saved to database");
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && typeof d === "object" && d.message) toast.message("Property already exists in your prospect database");
      else toast.error(d || "Could not save prospect");
    } finally { setSavingProspect(null); }
  };

  const addAsLead = async (p) => {
    setAdding(p.attom_id);
    try {
      const ct = contacts[p.attom_id] || {};
      const [fn, ...rest] = (p.owner_name || "").split(" ");
      const owner = (await api.post("/owners", { first_name: fn || "", last_name: rest.join(" ") || "", phone: (ct.phones || [])[0] || "", email: (ct.emails || [])[0] || "", ownership_verified: false })).data;
      const prop = (await api.post("/properties", {
        property_address: p.property_address, city: p.city, state: p.state, zip_code: p.zip_code,
        county: p.county, parcel_number: p.parcel_number, property_type: f.property_type || "Single Family",
        bedrooms: p.bedrooms, bathrooms: p.bathrooms, square_feet: p.square_feet, year_built: p.year_built,
        estimated_market_value: p.estimated_market_value, current_owner_id: owner.id,
      })).data;
      const lead = (await api.post("/leads", {
        owner_id: owner.id, property_id: prop.id, status: "New", priority: "Normal",
        lead_source: "Prospecting", deal_completeness_score: 30,
      })).data;
      if (p.estimated_loan_balance) {
        await api.post("/loans", { lead_id: lead.id, property_id: prop.id, estimated_current_balance: p.estimated_loan_balance, statement_verified: false });
      }
      toast.success("Lead created from prospect");
      nav(`/leads/${lead.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create lead");
    } finally { setAdding(null); }
  };

  return (
    <div>
      <PageHeader title="Prospecting" subtitle="Property prospecting intelligence — search, filter, verify and convert" />
      <div className="p-4 sm:p-6 lg:p-8">
        <Tabs defaultValue="database" className="space-y-6">
          <TabsList data-testid="prospecting-tabs">
            <TabsTrigger value="database" data-testid="tab-prospect-database"><Database className="w-3.5 h-3.5 mr-1.5" />Prospect Database</TabsTrigger>
            <TabsTrigger value="area" data-testid="tab-area-search"><Radar className="w-3.5 h-3.5 mr-1.5" />Area Search (ATTOM)</TabsTrigger>
          </TabsList>
          <TabsContent value="database"><ProspectDatabase /></TabsContent>
          <TabsContent value="area" className="space-y-6">
        <form onSubmit={search} className="bg-card border border-border rounded-md p-4 sm:p-6" data-testid="prospecting-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            <TextField label="ZIP / Postal Code" value={f.postalcode} onChange={set("postalcode")} testid="prospect-zip" placeholder="e.g. 90802" />
            <SelectField label="Property Type" value={f.property_type} onChange={set("property_type")} options={PROPERTY_TYPE} testid="prospect-type" />
            <TextField label="Min Bedrooms" type="number" value={f.min_beds} onChange={set("min_beds")} testid="prospect-beds" />
            <TextField label="Min Bathrooms" type="number" value={f.min_baths} onChange={set("min_baths")} testid="prospect-baths" />
            <SelectField label="Equity Level" value={f.equity_level} onChange={set("equity_level")} options={EQUITY_LEVELS} testid="prospect-equity-level" />
            <TextField label="Min Equity ($)" type="number" value={f.min_equity} onChange={set("min_equity")} testid="prospect-equity" />
            <TextField label="Last Sold — Min ($)" type="number" value={f.min_last_sold} onChange={set("min_last_sold")} testid="prospect-min-sold" />
            <TextField label="Last Sold — Max ($)" type="number" value={f.max_last_sold} onChange={set("max_last_sold")} testid="prospect-max-sold" />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <Checkbox checked={f.absentee} onCheckedChange={(v) => setF((p) => ({ ...p, absentee: !!v, owner_occupied: v ? false : p.owner_occupied }))} data-testid="prospect-absentee" />
                <span className="text-sm">Absentee / out-of-state owners</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <Checkbox checked={f.owner_occupied} onCheckedChange={(v) => setF((p) => ({ ...p, owner_occupied: !!v, absentee: v ? false : p.absentee }))} data-testid="prospect-owner-occupied" />
                <span className="text-sm">Owner-occupied only</span>
              </label>
            </div>
            <div className="flex flex-wrap gap-2">
              <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
                <DialogTrigger asChild><Button type="button" variant="outline" className="min-h-[44px]" data-testid="save-search-btn"><Bookmark className="w-4 h-4 mr-1.5" /> Save Search</Button></DialogTrigger>
                <DialogContent><DialogHeader><DialogTitle className="font-display">Save this search</DialogTitle></DialogHeader>
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Search name</Label>
                    <Input value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="e.g. Long Beach absentee SFR" data-testid="save-search-name" />
                  </div>
                  <DialogFooter><Button onClick={saveSearch} data-testid="save-search-submit">Save</Button></DialogFooter>
                </DialogContent>
              </Dialog>
              <Button type="submit" disabled={busy} data-testid="prospect-search-btn" className="min-h-[44px]">
                <Search className="w-4 h-4 mr-1.5" /> {busy ? "Searching…" : "Search Area"}
              </Button>
            </div>
          </div>
        </form>

        {saved.length > 0 && (
          <div className="flex flex-wrap items-center gap-2" data-testid="saved-searches">
            <span className="text-xs uppercase tracking-wider text-muted-foreground mr-1">Saved:</span>
            {saved.map((s) => (
              <span key={s.id} className="inline-flex items-center gap-0.5 bg-secondary rounded-full pl-3 pr-1 py-1 text-sm" data-testid={`saved-search-${s.id}`}>
                <button onClick={() => applySaved(s)} className="hover:text-accent transition-colors font-medium" data-testid={`apply-saved-${s.id}`}>{s.name}</button>
                <button onClick={() => toggleAlerts(s)} title={s.alerts_enabled ? "Alerts on" : "Enable alerts"} className={`w-7 h-7 inline-flex items-center justify-center rounded-full transition-colors ${s.alerts_enabled ? "text-accent" : "text-muted-foreground hover:bg-background"}`} data-testid={`alerts-toggle-${s.id}`}>
                  {s.alerts_enabled ? <BellRing className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5" />}
                </button>
                {s.alerts_enabled && <button onClick={() => checkNow(s)} title="Check for new matches" className="w-7 h-7 inline-flex items-center justify-center rounded-full text-muted-foreground hover:bg-background transition-colors" data-testid={`check-now-${s.id}`}><RefreshCw className="w-3.5 h-3.5" /></button>}
                <button onClick={() => deleteSaved(s)} className="w-7 h-7 inline-flex items-center justify-center rounded-full hover:bg-background transition-colors text-muted-foreground" data-testid={`delete-saved-${s.id}`}><X className="w-3.5 h-3.5" /></button>
              </span>
            ))}
          </div>
        )}

        {results === null && !busy && (
          <div className="flex flex-col items-center text-center text-muted-foreground py-16" data-testid="prospecting-empty">
            <Radar className="w-10 h-10 mb-3 text-accent/60" />
            <p className="max-w-md text-sm">Search a ZIP code to surface owners in that area. Filter by absentee owners and equity to focus on the most motivated sellers, skip-trace their contact, then add any prospect straight to your pipeline.</p>
          </div>
        )}

        {results && results.note && (
          <div className={`flex items-start gap-2 rounded-md px-4 py-3 text-sm ${results.provider_error ? "bg-amber-50 border border-amber-200 text-amber-800" : "bg-secondary text-muted-foreground"}`} data-testid="prospecting-note">
            {results.provider_error && <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}
            <span>{results.note}</span>
          </div>
        )}

        {results && results.results.length > 0 && selected.size > 0 && (
          <div className="sticky top-16 z-10 flex flex-wrap items-center justify-between gap-3 bg-primary text-primary-foreground rounded-md px-4 py-3" data-testid="bulk-action-bar">
            <span className="text-sm font-medium flex items-center gap-2"><Layers className="w-4 h-4 text-accent" /> {selected.size} selected</span>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" className="text-white/70 hover:text-white hover:bg-white/10" onClick={() => setSelected(new Set())} data-testid="bulk-clear">Clear</Button>
              <Button size="sm" onClick={bulkAdd} disabled={bulkBusy} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="bulk-add-btn">
                <Plus className="w-4 h-4 mr-1" />{bulkBusy ? "Adding…" : `Add ${selected.size} to Pipeline`}
              </Button>
            </div>
          </div>
        )}

        {results && (
          <div className="bg-card border border-border rounded-md overflow-x-auto" data-testid="prospecting-results">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary">
                  <TableHead className="w-10"><Checkbox checked={results.results.length > 0 && selected.size === results.results.length} onCheckedChange={toggleSelectAll} data-testid="select-all" aria-label="Select all" /></TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead className="hidden md:table-cell">Owner / Contact</TableHead>
                  <TableHead className="hidden sm:table-cell">Bd/Ba</TableHead>
                  <TableHead className="hidden xl:table-cell">Owned</TableHead>
                  <TableHead className="hidden lg:table-cell text-right">Est. Value</TableHead>
                  <TableHead className="hidden lg:table-cell text-right">Est. Equity</TableHead>
                  <TableHead className="hidden md:table-cell text-right">Target Offer</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {results.results.map((p) => {
                  const ct = contacts[p.attom_id];
                  return (
                  <TableRow key={p.attom_id} data-testid={`prospect-row-${p.attom_id}`} data-state={selected.has(p.attom_id) ? "selected" : undefined}>
                    <TableCell><Checkbox checked={selected.has(p.attom_id)} onCheckedChange={() => toggleSelect(p.attom_id)} data-testid={`select-${p.attom_id}`} aria-label="Select property" /></TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-start gap-2.5">
                        {svEnabled && (
                          <img src={`${API}/streetview?address=${encodeURIComponent(`${p.property_address}, ${p.city}, ${p.state} ${p.zip_code}`)}`}
                            alt="" loading="lazy" className="h-12 w-16 object-cover rounded shrink-0 border border-border"
                            onError={(e) => { e.currentTarget.style.display = "none"; }} data-testid={`prospect-photo-${p.attom_id}`} />
                        )}
                        <div>
                          <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-muted-foreground shrink-0" />{p.property_address}</span>
                          <span className="block text-xs font-normal text-muted-foreground">{p.city}, {p.state} {p.zip_code}{p.absentee && <Badge variant="outline" className="ml-2 bg-amber-100 text-amber-800 border-amber-200">Absentee</Badge>}</span>
                          <span className="sm:hidden block text-xs font-normal text-muted-foreground mt-0.5" data-testid={`prospect-bdba-mobile-${p.attom_id}`}>{p.bedrooms ?? "—"} bd · {p.bathrooms ?? "—"} ba</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground">
                      <div>{p.owner_name || "—"}</div>
                      {ct && <div className="text-xs mt-0.5 space-y-0.5" data-testid={`prospect-contact-${p.attom_id}`}>
                        {(ct.phones || []).map((ph) => <div key={ph} className="flex items-center gap-1 text-foreground"><Phone className="w-3 h-3 text-accent" />{ph}</div>)}
                        {(ct.emails || []).map((em) => <div key={em} className="flex items-center gap-1"><Mail className="w-3 h-3 text-accent" />{em}</div>)}
                        {ct.mocked && <div className="text-[10px] italic text-amber-700">demo data</div>}
                      </div>}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell font-mono-nums" data-testid={`prospect-bdba-${p.attom_id}`}>{p.bedrooms ?? "—"}/{p.bathrooms ?? "—"}</TableCell>
                    <TableCell className="hidden xl:table-cell font-mono-nums text-muted-foreground" data-testid={`prospect-owned-${p.attom_id}`}>
                      {p.years_owned != null ? `${p.years_owned} yrs` : "—"}
                      {p.last_sale_price != null && <span className="block text-[10px]">sold {usd(p.last_sale_price)}</span>}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-right font-mono-nums">{p.estimated_market_value != null ? usd(p.estimated_market_value) : "—"}</TableCell>
                    <TableCell className="hidden lg:table-cell text-right font-mono-nums text-accent">
                      {p.estimated_equity != null ? usd(p.estimated_equity) : "—"}
                      {p.equity_pct != null && <span className="block text-[10px] text-muted-foreground" data-testid={`prospect-equity-pct-${p.attom_id}`}>{p.equity_pct}% of value</span>}
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-right font-mono-nums font-medium" data-testid={`prospect-offer-${p.attom_id}`}>{p.target_offer != null ? usd(p.target_offer) : "—"}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {!ct && <Button size="sm" variant="ghost" disabled={tracing === p.attom_id} onClick={() => skipTrace(p)} data-testid={`prospect-trace-${p.attom_id}`}>
                        <PhoneCall className="w-3.5 h-3.5 mr-1" />{tracing === p.attom_id ? "Tracing…" : "Skip Trace"}
                      </Button>}
                      <Button size="sm" variant="ghost" disabled={savingProspect === p.attom_id} onClick={() => saveProspect(p)} data-testid={`prospect-save-db-${p.attom_id}`} title="Save to Prospect Database">
                        <Bookmark className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" disabled={adding === p.attom_id} onClick={() => addAsLead(p)} data-testid={`prospect-add-${p.attom_id}`}>
                        <Plus className="w-3.5 h-3.5 mr-1" />{adding === p.attom_id ? "Adding…" : "Add Lead"}
                      </Button>
                    </TableCell>
                  </TableRow>
                  );
                })}
                {results.results.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-10">No matching properties. Try a different ZIP or loosen the filters.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

